import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Upload, ImagePlus } from "lucide-react";

const TRADES = ["General Renovation", "Masonry", "Electrical", "Plumbing", "Painting", "Roofing", "Flooring", "Exterior Work", "Apartment Turnover", "Other"];
const TONES = ["Simple", "Professional", "Detailed", "Premium", "Short and direct"];

const empty = {
  company_name: "", contractor_name: "", phone: "", email: "", address: "",
  logo_url: "",
  default_disclaimer: "", default_validity: "14 days", default_tone: "Professional",
  default_trade: "General Renovation",
};

export default function CompanyProfilePage() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    api.get("/profile").then((r) => setForm({ ...empty, ...(r.data || {}) })).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let revoked;
    if (form.logo_url) {
      api.post("/files/sign", { path: form.logo_url })
        .then((r) => {
          // Backend returns relative URL like '/api/files/...?token=...';
          // prefix with the backend host so it works cross-origin too.
          setLogoPreview(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`);
          revoked = null;
        })
        .catch(() => setLogoPreview(null));
    } else {
      setLogoPreview(null);
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [form.logo_url]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/profile/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update("logo_url", r.data.logo_url);
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      toast.success("Profile saved.");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto" data-testid="profile-page">
      <h1 className="font-display text-3xl font-bold text-slate-900">Company profile</h1>
      <p className="text-slate-500 mt-1 mb-6">Appears at the top of every quote you generate.</p>

      <Card className="p-6 border-slate-200 space-y-5">
        {/* Logo upload */}
        <div className="flex items-center gap-5">
          <div className="h-20 w-20 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" className="h-full w-full object-contain" data-testid="logo-preview" />
            ) : (
              <ImagePlus className="h-7 w-7 text-slate-400" />
            )}
          </div>
          <div className="flex-1">
            <Label>Company logo</Label>
            <p className="text-xs text-slate-500 mt-0.5 mb-2">PNG, JPG, or WEBP up to 2&nbsp;MB. Appears on quotes and PDFs.</p>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoChange} data-testid="logo-upload-input" />
              <Button
                type="button" variant="outline" disabled={uploading}
                onClick={(e) => e.currentTarget.previousSibling.click()}
                data-testid="logo-upload-button"
              >
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {logoPreview ? "Replace logo" : "Upload logo"}
              </Button>
            </label>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Company name</Label><Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="profile-company-name" /></div>
          <div><Label>Contractor / owner name</Label><Input value={form.contractor_name} onChange={(e) => update("contractor_name", e.target.value)} data-testid="profile-contractor-name" /></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="profile-phone" /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="profile-email" /></div>
          <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => update("address", e.target.value)} data-testid="profile-address" /></div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Default tone</Label>
            <Select value={form.default_tone} onValueChange={(v) => update("default_tone", v)}>
              <SelectTrigger data-testid="profile-tone"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Default trade</Label>
            <Select value={form.default_trade} onValueChange={(v) => update("default_trade", v)}>
              <SelectTrigger data-testid="profile-trade"><SelectValue /></SelectTrigger>
              <SelectContent>{TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Quote validity</Label>
            <Input value={form.default_validity} onChange={(e) => update("default_validity", e.target.value)} data-testid="profile-validity" />
          </div>
        </div>

        <div>
          <Label>Default disclaimer</Label>
          <Textarea
            value={form.default_disclaimer}
            onChange={(e) => update("default_disclaimer", e.target.value)}
            className="min-h-[100px]"
            placeholder="Hidden damage or unforeseen conditions may require a change order…"
            data-testid="profile-disclaimer"
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-profile-button">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save profile
          </Button>
        </div>
      </Card>
    </div>
  );
}
